import { useState, useEffect } from 'react';
import { Mail, RefreshCw } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { api } from '../../lib/api';

interface ContactMessage {
  id: number;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  subject: string;
  message: string;
  createdAt: string;
}

export default function KontaktTab() {
  const { darkMode } = useDarkMode();
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get<ContactMessage[]>('/api/contact/messages');
      setMessages(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const card = darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Kontaktanfragen</h2>
        <button onClick={load} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {messages.length === 0 && !loading && (
        <div className={`text-center py-16 ${muted}`}>
          <Mail className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>Noch keine Kontaktanfragen.</p>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {messages.map((m) => (
          <div key={m.id} className={`rounded-xl border ${card}`}>
            <button
              className="w-full text-left px-5 py-4"
              onClick={() => setExpanded(expanded === m.id ? null : m.id)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{m.name}{m.company ? ` · ${m.company}` : ''}</p>
                  <p className={`text-sm truncate ${muted}`}>{m.email}{m.phone ? ` · ${m.phone}` : ''}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-medium">{m.subject}</p>
                  <p className={`text-xs ${muted}`}>{new Date(m.createdAt).toLocaleString('de-DE')}</p>
                </div>
              </div>
            </button>
            {expanded === m.id && (
              <div className={`px-5 pb-4 border-t ${darkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                <p className="mt-3 text-sm whitespace-pre-wrap">{m.message}</p>
                <a
                  href={`mailto:${m.email}`}
                  className="inline-block mt-3 text-sm text-blue-500 hover:underline"
                >
                  Antworten
                </a>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
